import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Sparkles, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * Admin-only panel that lists every Feature Flag and lets admins toggle
 * them on/off. The backend already gates non-admins from PATCHing, but we
 * also disable the switches for non-admin users so the UI is clear.
 */
export const FeatureFlagsPanel = () => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/feature-flags`);
      if (res.ok) {
        const data = await res.json();
        setFlags(data.flags || {});
      } else {
        toast.error('Failed to load feature flags');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, current) => {
    setBusyKey(key);
    try {
      const res = await authJsonFetch(`${API}/feature-flags/${key}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !current }),
      });
      if (res.ok) {
        toast.success(`${key} ${!current ? 'enabled' : 'disabled'}`);
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Toggle failed');
      }
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </CardContent>
      </Card>
    );
  }

  const keys = Object.keys(flags);

  return (
    <Card data-testid="feature-flags-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> Feature Flags
        </CardTitle>
        <CardDescription>
          Admins always have access to every flag below — toggle each one ON to release it to non-admin users. OFF means the feature stays admin-only while you build and validate it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No flags configured.</p>
        ) : keys.map(key => {
          const f = flags[key] || {};
          return (
            <div
              key={key}
              className="flex items-start justify-between gap-4 p-4 border rounded-lg"
              data-testid={`feature-flag-row-${key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-base">{f.label || key}</span>
                  {f.admin_only && (
                    <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> Admins always have access
                    </Badge>
                  )}
                  {f.enabled ? <Badge variant="default">Released to team</Badge> : <Badge variant="outline">Admin-only</Badge>}
                </div>
                {f.description && (
                  <p className="text-xs text-muted-foreground mt-1">{f.description}</p>
                )}
                {f.updated_by && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Last changed by {f.updated_by} on {new Date(f.updated_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
                  </p>
                )}
              </div>
              <Switch
                checked={!!f.enabled}
                onCheckedChange={() => toggle(key, !!f.enabled)}
                disabled={busyKey === key}
                data-testid={`feature-flag-switch-${key}`}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default FeatureFlagsPanel;
